import config from '../../config';
import { getAuthHeaders, getAuthHeaderOnly, handleResponse, handleNoContentResponse } from './common';

/**
 * Normalize a client insurance DTO from the backend (PascalCase) to camelCase.
 * Safe to call on already-normalized objects.
 */
function normalizeInsurance(dto) {
    if (!dto) return dto;
    return {
        insuranceProviderName:    dto.insuranceProviderName    ?? dto.InsuranceProviderName    ?? null,
        policyNumber:             dto.policyNumber             ?? dto.PolicyNumber             ?? null,
        groupNumber:              dto.groupNumber              ?? dto.GroupNumber              ?? null,
        subscriberName:           dto.subscriberName           ?? dto.SubscriberName           ?? null,
        subscriberDateOfBirth:    dto.subscriberDateOfBirth    ?? dto.SubscriberDateOfBirth    ?? null,
        relationshipToSubscriber: dto.relationshipToSubscriber ?? dto.RelationshipToSubscriber ?? null,
        notes:                    dto.notes                    ?? dto.Notes                    ?? null,
        cardFront:                normalizeCardMeta(dto.cardFront ?? dto.CardFront),
        cardBack:                 normalizeCardMeta(dto.cardBack  ?? dto.CardBack),
    };
}

function normalizeCardMeta(dto) {
    if (!dto) return null;
    return {
        fileName:    dto.fileName    ?? dto.FileName    ?? null,
        contentType: dto.contentType ?? dto.ContentType ?? null,
        uploadedAt:  dto.uploadedAt  ?? dto.UploadedAt  ?? null,
        url:         dto.url         ?? dto.Url         ?? null,
    };
}

/**
 * Client Insurance API
 * Manages structured insurance info and card images for a client.
 *
 * Backend spec: docs/BACKEND_CLIENT_INSURANCE_API.md
 */
const insuranceApi = {
    /**
     * Get insurance info for a client.
     * GET /clients/{clientReferenceId}/insurance
     *
     * Returns a normalized object with all fields null if no record exists yet.
     *
     * @param {string} clientReferenceId
     * @returns {Promise<Object>} Normalized insurance object
     */
    getInsurance: async (clientReferenceId) => {
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance`;
        const res = await fetch(url, { headers: getAuthHeaders() });
        const data = await handleResponse(res);
        return normalizeInsurance(data ?? {});
    },

    /**
     * Create or update insurance info for a client.
     * PUT /clients/{clientReferenceId}/insurance
     *
     * @param {string} clientReferenceId
     * @param {Object} payload
     * @param {string|null} payload.insuranceProviderName
     * @param {string|null} payload.policyNumber
     * @param {string|null} payload.groupNumber
     * @param {string|null} payload.subscriberName
     * @param {string|null} payload.subscriberDateOfBirth  ISO date string "YYYY-MM-DD"
     * @param {string|null} payload.relationshipToSubscriber
     * @param {string|null} payload.notes
     * @returns {Promise<void>}
     */
    upsertInsurance: async (clientReferenceId, payload) => {
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance`;
        const res = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload),
        });
        return handleNoContentResponse(res);
    },

    /**
     * Upload the front card image.
     * POST /clients/{clientReferenceId}/insurance/card-front
     *
     * @param {string} clientReferenceId
     * @param {File} file
     * @returns {Promise<Object>} { fileName, contentType, uploadedAt, url }
     */
    uploadCardFront: async (clientReferenceId, file) => {
        const form = new FormData();
        form.append('file', file);
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance/card-front`;
        const res = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaderOnly(),
            body: form,
        });
        const data = await handleResponse(res);
        return normalizeCardMeta(data);
    },

    /**
     * Upload the back card image.
     * POST /clients/{clientReferenceId}/insurance/card-back
     *
     * @param {string} clientReferenceId
     * @param {File} file
     * @returns {Promise<Object>} { fileName, contentType, uploadedAt, url }
     */
    uploadCardBack: async (clientReferenceId, file) => {
        const form = new FormData();
        form.append('file', file);
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance/card-back`;
        const res = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaderOnly(),
            body: form,
        });
        const data = await handleResponse(res);
        return normalizeCardMeta(data);
    },

    /**
     * Get a fresh presigned URL for the front card image.
     * GET /clients/{clientReferenceId}/insurance/card-front/url
     *
     * @param {string} clientReferenceId
     * @returns {Promise<string>} The presigned URL
     */
    getCardFrontUrl: async (clientReferenceId) => {
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance/card-front/url`;
        const res = await fetch(url, { headers: getAuthHeaders() });
        const data = await handleResponse(res);
        return data?.url ?? data?.Url ?? null;
    },

    /**
     * Get a fresh presigned URL for the back card image.
     * GET /clients/{clientReferenceId}/insurance/card-back/url
     *
     * @param {string} clientReferenceId
     * @returns {Promise<string>} The presigned URL
     */
    getCardBackUrl: async (clientReferenceId) => {
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance/card-back/url`;
        const res = await fetch(url, { headers: getAuthHeaders() });
        const data = await handleResponse(res);
        return data?.url ?? data?.Url ?? null;
    },

    /**
     * Delete the front card image.
     * DELETE /clients/{clientReferenceId}/insurance/card-front
     *
     * @param {string} clientReferenceId
     * @returns {Promise<void>}
     */
    deleteCardFront: async (clientReferenceId) => {
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance/card-front`;
        const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
        return handleNoContentResponse(res);
    },

    /**
     * Delete the back card image.
     * DELETE /clients/{clientReferenceId}/insurance/card-back
     *
     * @param {string} clientReferenceId
     * @returns {Promise<void>}
     */
    deleteCardBack: async (clientReferenceId) => {
        const url = `${config.api.url}/clients/${clientReferenceId}/insurance/card-back`;
        const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
        return handleNoContentResponse(res);
    },
};

export default insuranceApi;
